import React from 'react';
import styled from 'styled-components';
import Fonts from '../../../../../ui/global/Fonts';
import Colors from '../../../../../ui/global/Colors';

// Heuristic to detect likely filepaths (e.g., contains path separators and a file extension)
export const isLikelyFilePath = (value?: string) => {
  if (!value) return false;
  if (!(value.includes('/') || value.includes('\\'))) return false;
  const last = value.split(/[\\/]/).pop() || '';
  return /\.[A-Za-z0-9]+$/.test(last);
};

interface ChipProps extends React.HTMLAttributes<HTMLDivElement> {
  $bg?: string;
  $fg?: string;
}

const ChipContainer = styled.div<ChipProps>`
  background: ${({ $bg }) => $bg ?? (Colors.grey8+60 as any)};
  padding: 1px 6px;
  border-radius: 6px;
  font-family: ${Fonts.lexendSemiLight.fontFamily};
  font-weight: ${Fonts.lexendSemiLight.fontWeight};
  font-size: 11px;
  color: ${({ $fg }) => $fg ?? Colors.grey3};
  flex-shrink: 1;
  max-width: 100%;
  box-sizing: border-box;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: clip;
`;

const ChipInner = styled.span`
  direction: ltr;
  unicode-bidi: plaintext;
`;

export const LabelChip: React.FC<ChipProps> = ({ children, $bg, $fg, ...rest }) => {
  return (
    <ChipContainer $bg={$bg} $fg={$fg} {...rest}>
      <ChipInner>{children}</ChipInner>
    </ChipContainer>
  );
};

const MeasureSpan = styled.span`
  position: fixed;
  left: -99999px;
  top: -99999px;
  visibility: hidden;
  white-space: nowrap;
  pointer-events: none;
  font-family: ${Fonts.lexendSemiLight.fontFamily};
  font-weight: ${Fonts.lexendSemiLight.fontWeight};
  font-size: 11px;
`;

interface TruncatedPathLabelProps extends React.HTMLAttributes<HTMLDivElement> {
  path: string;
  $bg?: string;
  $fg?: string;
}

export const TruncatedPathLabel: React.FC<TruncatedPathLabelProps> = ({ path, $bg, $fg, ...rest }) => {
  const containerRef = React.useRef<HTMLDivElement>(null);
  const measureRef = React.useRef<HTMLSpanElement>(null);
  const [display, setDisplay] = React.useState<string>(path);

  React.useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const recompute = () => {
      const width = el.clientWidth || 0;
      // Compute available inner width by subtracting horizontal padding and a small safety margin
      const availableWidth = (() => {
        try {
          const styles = window.getComputedStyle(el);
          const padLeft = parseFloat(styles.paddingLeft || '0') || 0;
          const padRight = parseFloat(styles.paddingRight || '0') || 0;
          const safety = 2; // px safety to avoid half-glyph clipping
          return Math.max(width - padLeft - padRight - safety, 0);
        } catch (_) {
          // Fallback to hardcoded padding (6px left/right) + safety if computed style isn't available
          return Math.max(width - 12 - 2, 0);
        }
      })();

      if (availableWidth <= 0) {
        setDisplay(path);
        return;
      }
      const measure = (text: string): number => {
        if (!measureRef.current) return text.length * 8;
        measureRef.current.textContent = text;
        return measureRef.current.offsetWidth;
      };

      const sep = path.includes('/') ? '/' : (path.includes('\\') ? '\\' : '/');
      const parts = path.split(/[\\/]+/).filter(Boolean);
      const last = parts[parts.length - 1] || '';

      if (measure(path) <= availableWidth) {
        setDisplay(path);
        return;
      }

      let bestBoundaryCandidate = '';
      for (let t = parts.length; t >= 1; t--) {
        const tail = parts.slice(parts.length - t).join(sep);
        const candidate = `...${sep}${tail}`;
        if (measure(candidate) <= availableWidth) {
          bestBoundaryCandidate = candidate;
          break;
        }
      }
      if (bestBoundaryCandidate) {
        setDisplay(bestBoundaryCandidate);
        return;
      }

      // Never truncate inside the filename. If boundary truncation couldn't fit,
      // fall back to showing the full filename, optionally without a leading path.
      // If even the bare filename doesn't fit, still show it (it may be clipped by CSS),
      // but we will not elide characters from the filename itself.
      const bareFile = last;
      if (measure(bareFile) <= availableWidth) {
        setDisplay(bareFile);
        return;
      }
      setDisplay(bareFile);
    };

    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(() => recompute()) : null;
    ro?.observe?.(el as Element);
    recompute();
    return () => {
      ro?.disconnect?.();
    };
  }, [path]);

  return (
    <ChipContainer ref={containerRef} $bg={$bg} $fg={$fg} {...rest}>
      <MeasureSpan ref={measureRef} />
      <ChipInner>{display}</ChipInner>
    </ChipContainer>
  );
};

export default TruncatedPathLabel;


